import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {OfferInfo} from "~/app/core/model/offer-info";
import {momentNow, toDate} from "~/app/core/model/date-str";
import {OfferItemView} from "~/app/components/offer-item/offer-item-view";
import {OfferItemAction, OfferItemActionType} from "~/app/components/offer-item/offer-item-actions";
import * as Log from "~/app/core/log";
import {InteractionType} from "~/app/core/model/interaction-type";
import {InteractionComponent} from "~/app/core/model/interaction-component";
import {InteractionRefType} from "~/app/core/model/interaction-ref-type";
import {LocationService, LocationTracker} from "~/app/core/location.service";
import {LocationInfo} from "~/app/core/model/location-info";
import {Settings} from "~/app/core/model/settings";
import {AddressInfo} from "~/app/core/model/address-info";
import {isWithinWeeklyHours, toWeeklyHoursCouponString} from "~/app/core/model/weekly-hours";
import {toPlainString} from "~/app/core/model/formatted-string";
import {ActivatedRoute} from "@angular/router";
import {CoreService} from "~/app/core/core.service";

// Imported in offer.module.ts
@Component({
    selector: "OfferCouponOpen",
    templateUrl: "./offer-coupon-open.page.html",
    styleUrls: ["./offer-coupon-open.page.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfferCouponOpenPage implements OnInit, AfterViewInit, OnDestroy {
    hasMaps: boolean = false;

    showRedeemNow: boolean;
    unavailable: boolean;
    favoritable: boolean;
    showExpiration: boolean;
    expandedOffer: boolean;
    displayTime: moment.Moment = momentNow();

    isOnlineOnly: boolean = false;

    animating = false;
    showingBounceback = false;
    showingMore = false;
    showingRedeem = false;

    private checkOpenOfferTimeout;

    get canFavorite(): boolean {
        return this.offerInfo && (this.offerInfo.favorite || (this.offerInfo.favoriteCost <= this.core.api.tickets_getTicketCount()));
    }

    get favorited(): boolean {
        return this._offerInfo && this._offerInfo.id > 0 && !!this._offerInfo.favorite && !!this._offerInfo.revealed && this.settings.gamificationFavoriteMode;
    }

    get sample(): boolean {
        return this.offerInfo && this.offerInfo.sample && !!this.offerInfo.redeemedDate;
    }

    get testing(): boolean {
        const settings = this.settings;
        return settings && (settings.testable || settings.env === "test") && settings.testing;
    }

    get redeemButtonLabel(): string {
        if (!this.isOnlineOnly) return "Redeem";
        if (this.redeemed) return "Visit Website";
        else return "Redeem Now";
    }

    private _currentDate: Date = null;
    private _redeemed: boolean = null;
    private _daysRemaining: number = null;
    private _watchLocation: LocationTracker;
    private _offerInfo: OfferInfo;

    private longitude: number;
    private latitude: number;

    get settings(): Settings {
        return this.core.api.device_loadSettings();
    }

    get offerInfo(): OfferInfo {
        return this._offerInfo;
    }

    set offerInfo(offerInfo: OfferInfo) {
        if (this._offerInfo !== offerInfo) {
            // console.log("UPDATE OFFER INFO");
            this.isOnlineOnly = offerInfo && !!offerInfo.onlineOnly;
            this._offerInfo = offerInfo;
            this._daysRemaining = null;
            this.cd.detectChanges();
        }
    }

    get free(): boolean {
        // console.log("REFRESH ---------------------------- OfferCouponOpen) free:" + (this.offerInfo ? this.offerInfo.id : "NONE"));
        return OfferItemView.isOfferFree(this.offerInfo);
    }

    get shippedFree(): boolean {
        // console.log("REFRESH ---------------------------- OfferCoupon) free:" + (this.offerInfo ? this.offerInfo.id : "NONE"));
        return OfferItemView.isOfferShippedFree(this.offerInfo);
    }

    get shippedAdditional(): boolean {
        // console.log("REFRESH ---------------------------- OfferCoupon) free:" + (this.offerInfo ? this.offerInfo.id : "NONE"));
        return OfferItemView.isOfferShippedAdditional(this.offerInfo);
    }

    get freeBannerMessage(): string {
        if (!this.free) return null;
        if (this.isOnlineOnly) {
            if (this.shippedFree) {
                return "100% FREE! Shipping included.";
            } else if (this.shippedAdditional) {
                return null;
            }
        }
        return "100% FREE! No purchase necessary.";
    }

    set redeemed(redeemed: boolean) {
        this._redeemed = redeemed;
        this.cd.detectChanges();
    }

    get redeemed(): boolean {
        return this._redeemed != null ? this._redeemed : OfferItemView.isOfferRedeemed(this.offerInfo, this.settings.calculateCurrentTime());
    }

    get repeatUnlockable(): boolean {
        return this.settings.gamificationEnabled && OfferItemView.isOfferRepeatUnlockable(this.offerInfo, this.settings.calculateCurrentTime());
    }

    get redeemedInPast(): boolean {
        return this._redeemed != null ? this._redeemed : this.offerInfo && !!this.offerInfo.redeemedDate;
    }

    get active(): boolean {
        return !this.repeatUnlockable && !this.redeemed;
    }

    get repeatUnlockCost(): number {
        return this._offerInfo ? this._offerInfo.repeatUnlockCost : 0;
    }

    get canRepeatUnlock(): boolean {
        return this._offerInfo && (this._offerInfo.repeatUnlocked || (this._offerInfo.repeatUnlockCost <= this.core.api.tickets_getTicketCount()));
    }

    get reshow(): boolean {
        if (this.settings.allowReshowCashier) {
            const secondsSinceRedeem: number = OfferItemView.calculateSecondsSinceRedeem(this._currentDate, this.offerInfo);
            return secondsSinceRedeem !== null && secondsSinceRedeem < (60 * 60 * 24);
        } else {
            return false;
        }
    }

    get expired(): boolean {
        return this.daysRemaining === 0;
    }

    get daysRemaining(): number {
        if (this.showExpiration) {
            if (this._daysRemaining == null) {
                this._daysRemaining = OfferItemView.calculateDaysRemaining(this._currentDate, this.offerInfo);
            }
            return this._daysRemaining;
        } else {
            return null;
        }
    }

    get outsideOfHours(): boolean {
        return this.offerInfo && this.offerInfo.weeklyHours && !isWithinWeeklyHours(this.offerInfo.weeklyHours, this.displayTime);
    }

    get insideOfHours(): boolean {
        return this.offerInfo && this.offerInfo.weeklyHours && !this.outsideOfHours;
    }

    get solicitBounceback(): boolean {
        return OfferItemView.isOfferReadyForBounceback(this.settings.calculateCurrentTime(), this.offerInfo);
    }

    get numAddresses(): number {
        return this.offerInfo && this.offerInfo.addresses ? this.offerInfo.addresses.length : 0;
    }

    get offerAddresses(): Array<AddressInfo> {
        return this.offerInfo && this.offerInfo.addresses ? this.offerInfo.addresses : null;
    }

    get offerName(): string {
        return !this.offerInfo ? "" : this.offerInfo.name;
    }

    get offerTerms(): string {
        if (!this.offerInfo || !this.offerInfo.terms) {
            return "";
        } else {
            const description = this.offerInfo.terms.trim();
            return description;
        }
    }

    get offerAdditionalTerms(): string {
        if (!this.offerInfo || !this.offerInfo.additionalTerms || this.offerInfo.additionalTerms.length === 0) {
            return null;
        } else {
            const description = this.offerInfo.additionalTerms;
            return description;
        }
    }

    get offerWeeklyHours(): string {
        return !this.offerInfo || !this.offerInfo.weeklyHours ? null : toWeeklyHoursCouponString(this.offerInfo.weeklyHours);
    }

    get offerExpirationDate(): Date {
        return !this.offerInfo ? null : toDate(this.offerInfo.expirationDate);
    }

    get offerRedeemedDate(): Date {
        return !this.offerInfo ? null : toDate(this.offerInfo.redeemedDate);
    }

    get offerNextAvailableDate(): Date {
        return !this.offerInfo ? null : toDate(this.offerInfo.nextAvailableDate);
    }

    get offerWhatElse(): boolean {
        return this.offerInfo && !!this.offerInfo.whatElseDescription;
    }

    get offerWhatElseHeading(): string {
        return !this.offerInfo || !this.offerInfo.whatElseDescription ? null : this.offerInfo.whatElseDescription.heading;
    }

    get offerWhatElseDescription(): string {
        return !this.offerInfo || !this.offerInfo.whatElseDescription ? null : this.offerInfo.whatElseDescription.description;
    }

    get offerCompany(): string {
        return !this.offerInfo ? "" : this.offerInfo.companyName;
    }

    get offerCompanyDescription(): string {
        if (!this.offerInfo || !this.offerInfo.companyDescription || this.offerInfo.companyDescription.length === 0) {
            return null;
        } else {
            const description = toPlainString(this.offerInfo.companyDescription);
            return description;
        }
    }

    get nearestAddress(): AddressInfo {
        let minAddress: AddressInfo = null;
        if (this.offerInfo && this.offerInfo.addresses) {
            let minDistance = null;
            this.offerInfo.addresses.forEach((addr, index) => {
                const distance = this.milesAwayPrecise(addr);
                if (!minDistance || distance < minDistance) {
                    minDistance = distance;
                    minAddress = addr;
                }
            });
        }
        return minAddress;
    }

    get hasWebsite(): boolean {
        return this.offerInfo && this.offerInfo.companyWebUrl && this.offerInfo.companyWebUrl.length > 0;
    }

    get hasReviewSite(): boolean {
        return this.offerInfo && !!this.offerInfo.reviewUrl;
    }

    get hasReviewSiteLabel(): boolean {
        return this.reviewSiteLabel != null;
    }

    get companyWebsite(): string {
        return this.offerInfo.companyWebUrl;
    }

    get reviewSiteLabel(): string {
        if (this.offerInfo && this.offerInfo.reviewUrl) {
            if (this.offerInfo.reviewUrl.indexOf("yelp.com")) {
                return "https://yelp.com";
            }
            return this.offerInfo.reviewUrl;
        }
        return "";
    }

    get classList(): string {
        const classes = [];
        if (this.free) classes.push("type-free");
        else if (!this.free) classes.push("type-coupon");
        if (this.free && this.isOnlineOnly) classes.push("online-only");
        if (!(this.expired || this.unavailable || this.outsideOfHours) && !this.redeemed) classes.push("status-available");
        if (this.redeemed) classes.push("status-redeemed");
        if ((this.expired || this.unavailable || this.outsideOfHours) && !this.redeemed) {
            if (this.outsideOfHours) {
                classes.push("status-outofhours");
            } else {
                classes.push("status-expired");
            }
        }
        return classes.join(" ");
    }

    constructor(
        private readonly core: CoreService,
        private readonly route: ActivatedRoute,
        private readonly cd: ChangeDetectorRef,
        private readonly location: LocationService
    ) {
        // Use the component constructor to inject providers.
        this._currentDate = this.settings.calculateCurrentTime();

        setTimeout(() => {
            this.updateLocation();
        }, 50);
    }

    ngOnInit(): void {
        // nothing
        const offerId = Number(this.route.snapshot.paramMap.get("offerId"));
        console.log("offerId: " + offerId);
        this.offerInfo = this.core.api.offers_getOffer(offerId);
    }

    ngOnDestroy(): void {
        this.location.stopWatchingLocationInfo(this._watchLocation);
        this._watchLocation = null;
    }

    ngAfterViewInit(): void {
        this._watchLocation = this.location.startWatchingLocationInfo((where) => {
            if (where) {
                this.longitude = where.longitude;
                this.latitude = where.latitude;
                Log.trace(Log.Tag.OfferCouponOpen, "watchlong, watchlat: " + this.longitude + ", " + this.latitude);
            } else {
                this.longitude = null;
                this.latitude = null;
            }
            if (this.expandedOffer) {
                this.cd.detectChanges();
            }
        });
    }

    hasMilesAway(address: AddressInfo): boolean {
        return !!address.latitude && !!address.longitude && !!this.longitude && !!this.latitude;
    }

    getMilesAway(address: AddressInfo): number {
        const distance = this.milesAwayPrecise(address);
        if (distance) {
            return distance > 1 ? Math.round(distance) : Math.round(distance * 10) / 10;
        }
        return null;
    }

    milesAwayPrecise(address: AddressInfo): number {
        if (this.hasMilesAway(address)) {
            const distance = LocationInfo.milesAway(
                { longitude: address.longitude, latitude: address.latitude },
                { longitude: this.longitude, latitude: this.latitude }
            );
            return distance;
        }
        return null;
    }

    onAction(action: OfferItemAction): void {
    }

    onRedeemTap(): void {
        this.core.api.interaction(InteractionType.RedeemOffer, InteractionComponent.OfferDetails, InteractionRefType.Offer, this.offerInfo.id);
    }

    onRepeatUnlockTap(): void {
    }

    onRedeemNowTap(): void {
    }

    onShowCashierTap(): void {
        this.onAction({ type: OfferItemActionType.tapShowCashier });
    }

    onShareTap(): void {
        this.onAction({ type: OfferItemActionType.tapShare });
    }

    onFavoriteTap(): void {
        this.onAction({ type: OfferItemActionType.tapFavorite });
    }

    onMapTap(addr: AddressInfo): void {
        this.onAction({ type: OfferItemActionType.tapMap, data: addr });
    }

    onPhoneTap(phone: string): void {
        this.onAction({ type: OfferItemActionType.tapPhone, data: phone });
    }

    onWebsiteTap(): void {
        if (this.hasWebsite) {
            this.onAction({ type: OfferItemActionType.tapWebsite });
        }
    }

    onReviewTap(): void {
        this.onAction({ type: OfferItemActionType.tapReview });
    }

    tapThanksPage(): void {
        this.onAction({ type: OfferItemActionType.testThanks });
    }

    tapShowCashierPage(): void {
        this.onAction({ type: OfferItemActionType.testShowCashier });
    }

    tapResetOfferRedeemed(): void {
        this.onAction({ type: OfferItemActionType.testResetOfferRedeemed });
    }

    tapResetOfferRevealed(): void {
        this.onAction({ type: OfferItemActionType.testResetOfferRevealed });
    }

    tapResetOfferRepeatUnlocked(): void {
        this.onAction({ type: OfferItemActionType.testResetOfferRepeatUnlocked });
    }

    tapResetOfferThumbs(): void {
        this.onAction({ type: OfferItemActionType.testResetOfferThumbs });
    }

    tapResetOfferBounceback(): void {
        this.onAction({ type: OfferItemActionType.testResetOfferBounceback });
    }

    tapResetOfferVideo(): void {
        this.onAction({ type: OfferItemActionType.testResetOfferVideo });
    }

    private updateLocation() {
        try {
            if (this.expandedOffer) {
                this.location.isAllowLocation().then((allowed) => {
                    if (allowed) {
                        this.location.getLocationInfo().then((where) => {
                            if (where) {
                                this.longitude = where.longitude;
                                this.latitude = where.latitude;
                                this.cd.detectChanges();
                            }
                        });
                    }
                });
            }
        } catch (error) {
            // don't worry about this
        }
    }
}
